import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
/**
 * UserProfileBuilders — statically imported by index.tsx.
 *
 * Contains ONLY the render helpers (buildHero, buildSidebar, buildUserPhoneNav)
 * that are called inside the UserPage extend() override registered at app boot.
 * Keeping these separate prevents the heavy AvocadoUser*Page classes from
 * being pulled into the main bundle via the static import in index.tsx.
 *
 * The page components (AvocadoUserPostsPage etc.) live in UserProfilePage.tsx
 * and are loaded lazily via dynamic import() only when the user navigates to
 * a profile route.
 */
import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import Avatar from 'flarum/common/components/Avatar';
import AvatarEditor from 'flarum/forum/components/AvatarEditor';
import Dropdown from 'flarum/common/components/Dropdown';
import listItems from 'flarum/common/helpers/listItems';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import humanTime from 'flarum/common/helpers/humanTime';
import { coverPosition, safeCssUrl, trans } from '../utils';

// ─── Scrollable nav ───────────────────────────────────────────────────────────

class ScrollableNav {
  constructor() {
    _defineProperty(this, "_el", null);
    _defineProperty(this, "_canLeft", false);
    _defineProperty(this, "_canRight", false);
    _defineProperty(this, "_dragging", false);
    _defineProperty(this, "_startX", 0);
    _defineProperty(this, "_scrollLeft0", 0);
    _defineProperty(this, "_ro", null);
    _defineProperty(this, "_handleScroll", null);
    _defineProperty(this, "_handleMouseDown", null);
    _defineProperty(this, "_handleMouseMove", null);
    _defineProperty(this, "_handleMouseUp", null);
  }
  _check() {
    const el = this._el;
    if (!el) return;
    const l = el.scrollLeft > 1;
    const r = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (l !== this._canLeft || r !== this._canRight) {
      this._canLeft = l;
      this._canRight = r;
      m.redraw();
    }
  }
  _scroll(dir) {
    this._el?.scrollBy({
      left: dir * 160,
      behavior: 'smooth'
    });
  }
  oncreate(vnode) {
    const el = vnode.dom.querySelector('.AvocadoUserPage-navInner');
    this._el = el;
    if (!el) return;
    this._handleScroll = () => this._check();
    this._handleMouseDown = e => {
      this._dragging = true;
      this._startX = e.pageX - el.offsetLeft;
      this._scrollLeft0 = el.scrollLeft;
      document.documentElement.style.cursor = 'grabbing';
      document.documentElement.style.userSelect = 'none';
    };
    this._handleMouseMove = e => {
      if (!this._dragging) return;
      e.preventDefault();
      el.scrollLeft = this._scrollLeft0 - (e.pageX - el.offsetLeft - this._startX);
    };
    this._handleMouseUp = () => {
      if (!this._dragging) return;
      this._dragging = false;
      document.documentElement.style.cursor = '';
      document.documentElement.style.userSelect = '';
    };
    el.addEventListener('scroll', this._handleScroll, {
      passive: true
    });
    el.addEventListener('mousedown', this._handleMouseDown);
    window.addEventListener('mousemove', this._handleMouseMove);
    window.addEventListener('mouseup', this._handleMouseUp);
    this._ro = new ResizeObserver(() => this._check());
    this._ro.observe(el);
    this._check();
    const activeItem = el.querySelector('li.active');
    if (activeItem) {
      requestAnimationFrame(() => {
        el.scrollLeft = activeItem.offsetLeft - (el.clientWidth - activeItem.clientWidth) / 2;
      });
    }
  }
  onremove() {
    if (this._el) {
      this._el.removeEventListener('scroll', this._handleScroll);
      this._el.removeEventListener('mousedown', this._handleMouseDown);
    }
    window.removeEventListener('mousemove', this._handleMouseMove);
    window.removeEventListener('mouseup', this._handleMouseUp);
    this._ro?.disconnect();
    this._el = null;
  }
  view(vnode) {
    return m("div", {
      className: "AvocadoUserPage-nav"
    }, m("button", {
      className: `AvocadoUserPage-navArrow AvocadoUserPage-navArrow--left${this._canLeft ? ' is-visible' : ''}`,
      onclick: () => this._scroll(-1),
      "aria-label": extractText(app.translator.trans('ramon-avocado.forum.profile.scroll_left')),
      tabindex: "-1"
    }, m("i", {
      className: "fas fa-chevron-left",
      "aria-hidden": "true"
    })), vnode.children, m("button", {
      className: `AvocadoUserPage-navArrow AvocadoUserPage-navArrow--right${this._canRight ? ' is-visible' : ''}`,
      onclick: () => this._scroll(1),
      "aria-label": extractText(app.translator.trans('ramon-avocado.forum.profile.scroll_right')),
      tabindex: "-1"
    }, m("i", {
      className: "fas fa-chevron-right",
      "aria-hidden": "true"
    })));
  }
}

// ─── Shared: hero ─────────────────────────────────────────────────────────────

export function buildHero(user, isEditable, controls) {
  if (controls === void 0) {
    controls = [];
  }
  if (!user) {
    return m("div", {
      className: "AvocadoUserPage-hero AvocadoUserPage-hero--skeleton"
    }, m("div", {
      className: "AvocadoUserPage-cover"
    }), m("div", {
      className: "AvocadoUserPage-hero-inner"
    }, m("div", {
      className: "AvocadoUserPage-hero-bar"
    }, m("div", {
      className: "AvocadoUserPage-shimmer AvocadoUserPage-shimmer--avatar"
    }), m("div", {
      className: "AvocadoUserPage-hero-identity"
    }, m("div", {
      className: "AvocadoUserPage-shimmer AvocadoUserPage-shimmer--name"
    }), m("div", {
      className: "AvocadoUserPage-shimmer AvocadoUserPage-shimmer--meta"
    })))));
  }
  const color = user.color?.() || '#5a6480';
  const badges = user.badges?.().toArray?.() || [];
  const isOnline = user.isOnline?.();
  const lastSeen = user.lastSeenAt?.();
  const username = user.username?.();
  const joinTime = user.joinTime?.();

  // Locale do navegador (undefined) em vez de 'en-US' fixo, para respeitar pt-BR.
  const joinLabel = joinTime ? new Date(joinTime).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }) : null;

  // forumaker/profile-cover compat — a extensão estende UserCard.prototype.view,
  // mas o Avocado renderiza um hero próprio (não usa UserCard aqui). Lemos
  // user.cover() diretamente; se a extensão não estiver instalada o método
  // não existe e o optional-call retorna undefined. safeCssUrl rejeita
  // protocolos perigosos (data:, javascript:).
  const coverUrl = user.cover?.();
  const coverCss = coverUrl ? safeCssUrl(coverUrl) : null;
  const hasCover = coverCss !== null && coverCss !== 'none';
  const heroStyle = {
    '--user-color': color
  };
  if (hasCover) {
    heroStyle['--user-cover'] = coverCss;
    // Same reason as the cover URL itself: the extension applies the framing to
    // core's .UserCard, which this hero replaces. Without this the slider in the
    // cover editor moved nothing on the profile page.
    heroStyle['--user-cover-position'] = coverPosition(user);
  }
  const onlineLabel = trans('ramon-avocado.forum.user.online', 'Online');
  const bioNode = (() => {
    try {
      const UserBio = flarum.reg.get('fof-user-bio', 'forum/components/UserBio');
      if (UserBio && user.attribute('canViewBio')) {
        return m("div", {
          className: "AvocadoUserPage-hero-bio"
        }, m(UserBio, {
          user: user,
          editable: isEditable
        }));
      }
    } catch {}
    return null;
  })();

  // Layout estilo Facebook: capa larga, avatar grande sobreposto à esquerda,
  // identidade (nome + @handle + stats) à direita, ações na extrema direita —
  // tudo numa faixa horizontal; a bio fica abaixo, em largura cheia.
  return m("div", {
    className: 'AvocadoUserPage-hero' + (hasCover ? ' AvocadoUserPage-hero--hasCover' : ''),
    style: heroStyle
  }, m("div", {
    className: "AvocadoUserPage-cover"
  }), m("div", {
    className: "AvocadoUserPage-hero-inner"
  }, m("div", {
    className: "AvocadoUserPage-hero-bar"
  }, m("div", {
    className: `AvocadoUserPage-hero-avatarWrap${isOnline ? ' AvocadoUserPage-hero-avatarWrap--online' : ''}`,
    title: isOnline ? onlineLabel : undefined,
    "aria-label": isOnline ? onlineLabel : undefined
  }, isEditable ? m(AvatarEditor, {
    user: user
  }) : m(Avatar, {
    user: user,
    loading: "eager"
  })), m("div", {
    className: "AvocadoUserPage-hero-identity"
  }, m("div", {
    className: "AvocadoUserPage-hero-nameRow"
  }, m("h1", {
    className: "AvocadoUserPage-hero-name"
  }, user.displayName?.() || username), badges.length > 0 && m("ul", {
    className: "AvocadoUserPage-hero-badges badges"
  }, listItems(badges))), username && m("span", {
    className: "AvocadoUserPage-hero-username"
  }, "@", username), m("div", {
    className: "AvocadoUserPage-hero-meta"
  }, joinLabel && m("span", {
    className: "AvocadoUserPage-metaItem"
  }, m("i", {
    className: "far fa-calendar",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.profile.member_since', 'Member since {date}', {
    date: joinLabel
  })), !isOnline && lastSeen && m("span", {
    className: "AvocadoUserPage-metaItem"
  }, m("i", {
    className: "far fa-clock",
    "aria-hidden": "true"
  }), humanTime(lastSeen)))), controls.length > 0 && m("div", {
    className: "AvocadoUserPage-hero-actions"
  }, m(Dropdown, {
    buttonClassName: "Button AvocadoUserPage-controlsBtn",
    menuClassName: "Dropdown-menu--right",
    label: app.translator.trans('core.forum.user_controls.button')
  }, controls))), bioNode));
}

// ─── Shared nav builders ──────────────────────────────────────────────────────

export function buildSidebar(page) {
  const user = page?.user;
  if (!user) {
    return m("div", {
      className: "AvocadoUserPage-nav"
    }, m("div", {
      className: "AvocadoUserPage-navInner"
    }, [1, 2, 3, 4].map(i => m("div", {
      key: i,
      className: "AvocadoUserPage-shimmer AvocadoUserPage-shimmer--navItem"
    }))));
  }
  return m(ScrollableNav, null, m("ul", {
    className: "AvocadoUserPage-navInner"
  }, listItems(page.navItems().toArray())));
}
export function buildUserPhoneNav(page) {
  const user = page?.user;
  const items = user ? page.navItems().toArray() : [];
  return m("nav", {
    className: "IndexPage-nav sideNav"
  }, m("ul", null, m("li", {
    className: "item item-nav"
  }, m(SelectDropdown, {
    className: "App-titleControl",
    buttonClassName: "Button"
  }, items))));
}
flarum.reg.add('ramon-avocado', 'forum/components/UserProfileBuilders', { buildHero: buildHero,buildSidebar: buildSidebar,buildUserPhoneNav: buildUserPhoneNav, });